import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'kurso_cookie_consent';

// ─── Synchronous read of stored consent ───────────────────────────────────────
function readStoredPrefs() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    // Validate: must have necessary: true and updatedAt (guards against stale/broken values)
    if (!parsed || typeof parsed !== 'object' || parsed.necessary !== true || !parsed.updatedAt) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  // Synchronous initialisation — banner state determined BEFORE first render (no flicker)
  const [prefs, setPrefs] = useState(() => readStoredPrefs());
  const [showBanner, setShowBanner] = useState(() => readStoredPrefs() === null);
  const [showCustom, setShowCustom] = useState(false);

  // Debug log as required by specification
  useEffect(() => {
    console.log('CookieBanner mounted, consent status:', localStorage.getItem(STORAGE_KEY));
  }, []);

  // Apply Mixpanel based on consent
  useEffect(() => {
    if (prefs?.analytics === true) {
      initMixpanel();
    } else if (prefs?.analytics === false) {
      optOutMixpanel();
    }
  }, [prefs]);

  const savePrefs = useCallback((newPrefs) => {
    const updated = { ...newPrefs, necessary: true, updatedAt: new Date().toISOString() };
    setPrefs(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setShowBanner(false);
    setShowCustom(false);
    if (updated.analytics) {
      initMixpanel();
    } else {
      optOutMixpanel();
    }
  }, []);

  const acceptAll = useCallback(() => {
    savePrefs({ necessary: true, analytics: true, marketing: true });
  }, [savePrefs]);

  const rejectAll = useCallback(() => {
    savePrefs({ necessary: true, analytics: false, marketing: false });
  }, [savePrefs]);

  const reopenBanner = useCallback(() => {
    setShowCustom(false);
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{
      prefs, showBanner, showCustom, setShowCustom,
      acceptAll, rejectAll, savePrefs, reopenBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}

// ─── Mixpanel helpers ─────────────────────────────────────────────────────────
function initMixpanel() {
  try {
    const token = process.env.REACT_APP_MIXPANEL_TOKEN;
    if (!token || typeof window.mixpanel === 'undefined') return;
    if (window._mixpanelInitialized) return;
    window.mixpanel.init(token, { debug: false, track_pageview: true });
    window.mixpanel.opt_in_tracking();
    window._mixpanelInitialized = true;
  } catch { /* ignore */ }
}

function optOutMixpanel() {
  try {
    if (typeof window.mixpanel !== 'undefined') {
      window.mixpanel.opt_out_tracking();
    }
    window._mixpanelInitialized = false;
  } catch { /* ignore */ }
}
